import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle, Download, ExternalLink, RotateCcw } from 'lucide-react';
import { hrApplicationDetail, hrDecideApplication, rescreenSubmission } from '../../lib/jobsApi';
import type { HrApplicationDetail, HrDecisionResult, HrScreening, SubmissionType } from '../../lib/jobsApi';
import { ApiError } from '../../lib/api';
import { formatRelativeDate } from '../../lib/formatRelativeDate';
import { hrErrorMessage } from '../../lib/tenancyErrorCopy';
import { markScreeningQueued } from './applicantDetailState';
import { stageLabel, viewedLabel } from './stageLabels';
import { candidateStageLabel } from '../../lib/candidateStageLabels';
import { offeredDecisions, requiresWrittenReason } from '../../lib/decisionOffers';
import type { DecisionOffer, OfferedAction } from '../../lib/decisionOffers';
import { useAuth } from '../../contexts/AuthContext';

/*
 * Velaigal /hr/applications/:id — the single-candidate review screen.
 *
 * Candidate + resume + per-task submissions are a read-only render of
 * hrApplicationDetail; the only interactive parts are the per-submission
 * re-screen and the action bar at the bottom, which writes through
 * POST /api/hr/applications/:id/decisions. Opening this page records a VIEW,
 * never a decision — `stage` moves only through that POST.
 *
 * The decision carries the stage and aggregate version THIS PAGE RENDERED,
 * so a colleague deciding first, or a screening landing after the page
 * loaded, is answered 409 rather than silently overwritten. Both 409s are
 * dead ends here on purpose: they set a blocking notice and disable the
 * buttons, because re-sending the same expectations is precisely the
 * overwrite the guard exists to prevent. Only a reload clears them.
 *
 * hrReview.ts on the server can return `resume: null` (the application's
 * resume row went missing), but jobsApi.ts types `resume` as always-present
 * to keep the common case simple. Rather than widen that shared contract,
 * this page narrows locally via ApplicationDetailData and renders the
 * resume block only when it's actually there.
 */

type ApplicationDetailData = Omit<HrApplicationDetail, 'resume'> & {
  resume: HrApplicationDetail['resume'] | null;
};

const SUBMISSION_TYPE_LABELS: Record<SubmissionType, string> = {
  hosted_link: 'Hosted link',
  repo: 'Repository',
  video: 'Video',
  document: 'Document',
};

const PROFILE_FIELD_LABEL: Record<string, string> = {
  title: 'Title',
  phone: 'Phone',
  location: 'Location',
  bio: 'Bio',
};
const PROFILE_FIELD_ORDER = ['title', 'phone', 'location', 'bio'];

const StageChip: React.FC<{ stage: string }> = ({ stage }) => {
  const isSaffron = stage === 'shortlisted' || stage === 'interviewing' || stage === 'hired';
  return (
    <span
      className="chip"
      style={isSaffron ? undefined : { backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' }}
    >
      <span
        aria-hidden="true"
        className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: isSaffron ? 'var(--color-saffron-press)' : 'var(--color-ink-tertiary)' }}
      />
      {stageLabel(stage)}
    </span>
  );
};

// tone 'cream' is for spinners inside dark-filled buttons (.btn-primary,
// .btn-danger); tone 'ink' is for spinners inside .btn-secondary, whose
// background is transparent/paper, not dark — cream-on-cream would be
// invisible there.
const InlineSpinner: React.FC<{ label: string; tone?: 'cream' | 'ink' }> = ({ label, tone = 'cream' }) => (
  <>
    <span
      className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
      style={
        tone === 'ink'
          ? { borderColor: 'rgba(26, 22, 20, 0.2)', borderTopColor: 'var(--color-ink)' }
          : { borderColor: 'rgba(244, 240, 232, 0.35)', borderTopColor: 'var(--color-cream)' }
      }
      aria-hidden="true"
    />
    <span>{label}</span>
  </>
);

const LoadingNotice: React.FC = () => (
  <div role="status" className="max-w-3xl mx-auto text-center py-24">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-4 text-sm text-ink-tertiary">Loading this applicant.</p>
  </div>
);

const NotFoundNotice: React.FC = () => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3">Not found</span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      This application isn&apos;t available.
    </h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">
      It may have been removed, or you don&apos;t have access to it.
    </p>
    <Link to="/hr/jobs" className="btn-primary">
      Back to your jobs
    </Link>
  </div>
);

const LoadErrorNotice: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      Couldn&apos;t load this applicant.
    </h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary">
      Try again
    </button>
  </div>
);

// Screenings that can be re-run: in-flight statuses (queued/running) are
// rejected server-side with a 409 conflict, so the button is simply not
// offered for those — matching IN_FLIGHT_SCREENING_STATUSES on the server.
const RESCREENABLE_STATUSES = new Set(['done', 'failed', 'manual_review']);

const NeedsReviewChip: React.FC = () => (
  <span className="chip" style={{ backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' }}>
    Needs review
  </span>
);

const LinkDeadNote: React.FC = () => (
  <p className="text-xs text-ink-tertiary mt-2">Link was not reachable at screening time.</p>
);

// Pure render of one screening's report, independent of the re-screen
// affordance below it (that lives in SubmissionCard, which asks the PARENT to
// flip the screening to queued — the card holds no screening state of its own).
const ScreeningReport: React.FC<{ screening: HrScreening }> = ({ screening }) => {
  const { status, score, summary, linkAlive, error, report, screenshotUrls } = screening;

  if (status === 'done') {
    // HrScreeningReport is a closed, server-picked shape now (C2's allowlist),
    // but it is still JSON off the wire rather than something this file
    // validates — a malformed row (criteria/redFlags present but not an
    // array) must not reach .map(); there's no ErrorBoundary here to catch
    // that crash.
    const criteria = Array.isArray(report?.criteria) ? report.criteria : [];
    const redFlags = Array.isArray(report?.redFlags) ? report.redFlags : [];
    return (
      <div className="space-y-3">
        <div className="flex items-baseline gap-3 flex-wrap">
          {score !== null && <p className="text-ink font-semibold text-lg">{score} / 100</p>}
          <span className="label-editorial">AI assessment — advisory</span>
        </div>
        {summary && <p className="text-sm text-ink-secondary leading-relaxed">{summary}</p>}
        {criteria.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-hairline">
                  <th className="label-editorial py-1.5 pr-3 font-medium">Criterion</th>
                  <th className="label-editorial py-1.5 pr-3 font-medium">Weight</th>
                  <th className="label-editorial py-1.5 pr-3 font-medium">Score</th>
                  <th className="label-editorial py-1.5 font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {criteria.map((c, i) => (
                  <tr key={i} className="border-b border-hairline last:border-0">
                    <td className="text-sm text-ink py-1.5 pr-3">{c.criterion}</td>
                    <td className="text-sm text-ink-secondary py-1.5 pr-3">{c.weight}</td>
                    <td className="text-sm text-ink-secondary py-1.5 pr-3">{c.score0to10} / 10</td>
                    <td className="text-sm text-ink-secondary py-1.5">{c.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {redFlags.length > 0 && (
          <div role="alert" className="px-3 py-3 border border-warning rounded bg-paper">
            <ul className="space-y-1">
              {redFlags.map((flag, i) => (
                <li key={i} className="text-sm text-ink flex items-start gap-2">
                  <span
                    aria-hidden="true"
                    className="inline-block w-1.5 h-1.5 rounded-full bg-warning flex-shrink-0 mt-1.5"
                  />
                  {flag}
                </li>
              ))}
            </ul>
          </div>
        )}
        {screenshotUrls && screenshotUrls.length > 0 && (
          <div>
            <span className="label-editorial block mb-2">Screenshots</span>
            <div className="flex gap-2 overflow-x-auto">
              {screenshotUrls.map((url, i) => (
                <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img
                    src={url}
                    alt={`App screenshot ${i + 1}`}
                    loading="lazy"
                    className="rounded border border-hairline"
                    style={{ maxHeight: 160 }}
                  />
                </a>
              ))}
            </div>
          </div>
        )}
        {report?.probed === true && (
          <p className="text-sm text-ink-tertiary">Probe interaction ran — see the last screenshot.</p>
        )}
        {Array.isArray(report?.consoleErrors) && report.consoleErrors.length > 0 && (
          <p className="text-ink-tertiary text-sm">{report.consoleErrors.length} console errors captured</p>
        )}
        {linkAlive === false && <LinkDeadNote />}
      </div>
    );
  }

  if (status === 'manual_review') {
    return (
      <div className="space-y-2">
        <NeedsReviewChip />
        {summary ? (
          <p className="text-sm text-ink-secondary leading-relaxed">{summary}</p>
        ) : error ? (
          <p className="text-ink-tertiary text-sm">
            Automatic screening couldn&apos;t run ({error}) — assess manually.
          </p>
        ) : null}
        {linkAlive === false && <LinkDeadNote />}
      </div>
    );
  }

  if (status === 'failed') {
    // Raw infra error strings (ECONNREFUSED, stack fragments, etc.) must never reach an HR
    // tenant — `error` stays server-side for ops to read from the DB/logs. Only the fixed
    // copy renders here regardless of what the server recorded.
    return (
      <div className="space-y-1">
        <p className="text-sm text-ink">Screening failed after retries.</p>
      </div>
    );
  }

  if (status === 'running') {
    return <p className="text-sm text-ink-tertiary">Screening running — results will appear here.</p>;
  }

  // queued — the pipeline is real, so this is a transient state rather than the old
  // Phase-2B-2 "not built yet" placeholder.
  return <p className="text-sm text-ink-tertiary">Screening queued — results will appear here.</p>;
};

interface SubmissionCardProps {
  submission: HrApplicationDetail['submissions'][number];
  onRescreenQueued: (submissionId: string) => void;
}

/*
 * Renders `submission.screening` straight from the prop. The only local state is the in-flight
 * flag for the re-screen POST; the optimistic "now queued" flip is handed up to the parent, which
 * owns the fetched detail. Copying the screening into local state here would shadow the prop for
 * the lifetime of the card.
 */
const SubmissionCard: React.FC<SubmissionCardProps> = ({ submission, onRescreenQueued }) => {
  const [rescreening, setRescreening] = useState(false);
  const screening = submission.screening;

  const handleRescreen = async () => {
    if (rescreening) return;
    setRescreening(true);
    try {
      await rescreenSubmission(submission.id);
      onRescreenQueued(submission.id);
      toast.success('Re-screening queued.');
    } catch (err) {
      toast.error(hrErrorMessage(err, 'Could not start re-screening.'));
    } finally {
      setRescreening(false);
    }
  };

  return (
    <article className="card">
      <span className="label-editorial block mb-1" style={{ color: 'var(--color-saffron)' }}>
        {SUBMISSION_TYPE_LABELS[submission.type]}
      </span>
      <h3 className="font-display text-lg font-medium text-ink">{submission.task.title}</h3>

      <div className="flex flex-wrap items-center gap-3 mt-3">
        {submission.url && (
          <a
            href={submission.url}
            target="_blank"
            rel="noopener noreferrer"
            className="velaigal-link font-medium text-sm inline-flex items-center gap-1.5"
          >
            Open submission
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
          </a>
        )}
        {submission.downloadUrl && (
          <a
            href={submission.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
            style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
          >
            <Download className="h-3 w-3" strokeWidth={1.75} />
            Download document
          </a>
        )}
      </div>

      {submission.note.trim() && (
        <p className="text-sm text-ink-secondary mt-3 leading-relaxed whitespace-pre-line">&ldquo;{submission.note}&rdquo;</p>
      )}

      {screening && (
        <div className="mt-4 pt-4 border-t border-hairline">
          <span className="label-editorial block mb-2">Screening</span>
          <ScreeningReport screening={screening} />
          {RESCREENABLE_STATUSES.has(screening.status) && (
            <button
              type="button"
              className="btn-secondary mt-3"
              style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
              onClick={() => void handleRescreen()}
              disabled={rescreening}
            >
              {rescreening ? <InlineSpinner label="Re-screening…" tone="ink" /> : 'Re-screen'}
            </button>
          )}
        </div>
      )}
    </article>
  );
};

interface ActionBarProps {
  applicationId: string;
  stage: string;
  aggregateVersion: number;
  /* The caller's role at the company that owns this application, read off the ACTIVE membership
   * (AuthContext's activeCompany) — the same tenant ?companyId= carries, so it is the role the
   * server will resolve too. Every recruiter edge in TRANSITIONS is requiresOwner: true, so this
   * is what separates a live control from one that would come back 403 insufficient_company_role.
   * null only in the sliver before the session resolves, and the owner-only controls fail CLOSED
   * on it: an enabled button a member cannot use is the dead end this change exists to remove.
   */
  companyRole: 'owner' | 'member' | null;
  onDecided: (result: HrDecisionResult) => void;
  onReload: () => void;
}

/* Reject reasons are the recruiter-facing subset of decisions_reason_code_check. A rejection
 * ALSO needs a written rationale: decisions_rationale_check refuses a terminal decision with a
 * blank one, so the button stays disabled until there is something to record. Shortlisting is
 * not terminal and does not, which is why it stays a single confirm click. */
const REJECT_REASONS: Array<{ value: string; label: string }> = [
  { value: 'weak_work', label: 'The work did not meet the bar' },
  { value: 'insufficient_evidence', label: 'Not enough evidence to judge' },
  { value: 'role_requirements_unmet', label: 'Requirements not met' },
  { value: 'stronger_candidates', label: 'Stronger candidates in the pool' },
  { value: 'role_filled', label: 'Role already filled' },
  { value: 'integrity_concern', label: 'Integrity concern' },
];

/* A reopen offers no reason menu. None of decisions_reason_code_check's values means "that
 * rejection was a mistake" — they are all exit reasons — and inventing one would put a false
 * claim in an append-only row. 'other' is the honest code, and it is also what makes the written
 * reason doubly required (the edge's own requiresRationale AND the CHECK's 'other' branch), which
 * is exactly right on the one move that takes a decision back. */
const REOPEN_REASON_CODE = 'other';

/* Keyed by the stage the SERVER recorded, not by the action the click asked for. */
const RECORDED_TOAST: Record<string, string> = {
  shortlisted: 'Candidate shortlisted.',
  rejected: 'Candidate rejected.',
  under_review: 'Reopened — this candidate is back under review.',
};
const FAILED_VERB: Record<OfferedAction, string> = {
  shortlist: 'shortlist', reject: 'reject', reopen: 'reopen',
};

/*
 * The two 409s the decision route can answer, and why they are ONE kind of outcome with TWO
 * readings rather than two kinds of error.
 *
 * Both mean the same thing about this page: what it is showing is not what the server has, so the
 * recruiter is about to decide on something they have not actually seen. Neither is retryable —
 * re-sending the same expectedStage/expectedAggregateVersion earns the identical refusal, and a
 * client that retried anyway would be asking for exactly the overwrite the guard exists to
 * prevent. So they render as a blocking notice with a reload, never as a toast that fades and
 * leaves a live button behind.
 *
 * They are kept apart because they answer different questions. stale_view means something MOVED
 * underneath you — a colleague's decision, or a screening result bumping aggregate_version.
 * invalid_transition means the edge itself does not exist from where this candidate really is;
 * the server's own sentence names that edge, so it is shown verbatim underneath.
 *
 * invalid_transition is no longer reachable by pressing a button on a FRESH render — the button
 * set is derived from TRANSITIONS (offeredDecisions), so nothing offered is an edge the graph
 * forbids. It is still handled and still distinct: the props describe the row as it was RENDERED,
 * so a colleague moving the candidate underneath a stale page still produces it.
 */
type DecisionBlock = { code: 'stale_view' | 'invalid_transition'; detail: string | null };

const BLOCK_COPY: Record<DecisionBlock['code'], { label: string; body: string }> = {
  stale_view: {
    label: 'Out of date',
    body: 'Nothing was recorded. This candidate changed while the page was open — a colleague may have decided, or a new screening result may have landed since it loaded. Reload and look again before deciding.',
  },
  invalid_transition: {
    label: 'Not a move that exists',
    body: 'Nothing was recorded. This candidate is not where the page says they are, so the move you pressed is not one that exists from their real stage. Reload and look again.',
  },
};

/*
 * `stage` and `aggregateVersion` arrive as PROPS, off the detail this page already rendered.
 * They are deliberately not re-read at click time: a fresh GET would fetch whatever is true now
 * and hand the server a precondition the recruiter never looked at, which turns the guard into a
 * rubber stamp. The pair that travels with the decision is the pair that was on screen. The same
 * goes for the OFFERED MOVES — offeredDecisions reads that same rendered `stage`, so the button
 * set and the precondition can never describe two different rows.
 */
const ActionBar: React.FC<ActionBarProps> = ({ applicationId, stage, aggregateVersion, companyRole, onDecided, onReload }) => {
  const [confirming, setConfirming] = useState<OfferedAction | null>(null);
  const [reasonCode, setReasonCode] = useState(REJECT_REASONS[0].value);
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState<DecisionBlock | null>(null);
  const [decided, setDecided] = useState<HrDecisionResult | null>(null);

  const decide = async (offer: DecisionOffer) => {
    if (busy || blocked) return;
    setBusy(true);
    const code = offer.action === 'reject' ? reasonCode
      : offer.action === 'reopen' ? REOPEN_REASON_CODE
        : 'strong_work';
    try {
      const result = await hrDecideApplication(applicationId, {
        action: offer.action,
        toStage: offer.toStage,
        reasonCode: code,
        rationale: requiresWrittenReason(offer, code) ? rationale.trim() : '',
        expectedStage: stage,
        expectedAggregateVersion: aggregateVersion,
      });
      // The RECORDED edge, not the requested one. They agree today; reading it off the response
      // is what keeps this screen honest if they ever stop agreeing.
      onDecided(result);
      setDecided(result);
      setConfirming(null);
      setRationale('');
      toast.success(RECORDED_TOAST[result.decision.toStage] ?? 'Decision recorded.');
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'stale_view' || err.code === 'invalid_transition')) {
        setBlocked({ code: err.code, detail: err.code === 'invalid_transition' ? err.message : null });
        setConfirming(null);
      } else {
        toast.error(hrErrorMessage(err, `Could not ${FAILED_VERB[offer.action]} this candidate.`));
      }
    } finally {
      setBusy(false);
    }
  };

  /*
   * THE BUTTON SET IS THE GRAPH'S, not a second list beside it. offeredDecisions intersects the
   * three moves this bar implements with the recruiter edges TRANSITIONS actually contains from
   * the rendered stage: `hired` and `withdrawn` offer nothing, `rejected` offers only the reopen,
   * and `shortlisted` only the reject. The rule that used to live here was a pair of inequality
   * tests against two stage names — a second source of truth, and it disagreed with the machine
   * on three of the seven stages. Nothing in this component may compare `stage` to a literal
   * again; src/pages/HR/decisionWiring.test.ts sweeps for exactly that.
   */
  const offers = offeredDecisions(stage);
  const offerOf = (action: OfferedAction) => offers.find((o) => o.action === action) ?? null;
  const shortlistOffer = offerOf('shortlist');
  const rejectOffer = offerOf('reject');
  const reopenOffer = offerOf('reopen');
  const isOwner = companyRole === 'owner';
  const ownerBlocked = !isOwner && offers.some((o) => o.requiresOwner);

  return (
    <div className="mt-10 pt-8 border-t border-hairline">
      {blocked && (
        <div role="alert" className="mb-5 max-w-lg bg-paper border border-hairline rounded-md p-4">
          <span className="label-editorial block mb-2" style={{ color: 'var(--color-warning)' }}>
            {BLOCK_COPY[blocked.code].label}
          </span>
          <p className="text-sm text-ink leading-relaxed">{BLOCK_COPY[blocked.code].body}</p>
          {blocked.detail && <p className="text-sm text-ink-secondary leading-relaxed mt-2">{blocked.detail}</p>}
          <button type="button" className="btn-secondary mt-4" onClick={onReload}>
            Reload this candidate
          </button>
        </div>
      )}

      {confirming === 'reject' && (
        <div className="mb-5 space-y-3 max-w-lg">
          <div className="space-y-2">
            <label htmlFor="reject-reason" className="block text-sm font-medium text-ink">Reason</label>
            <select
              id="reject-reason" className="input" value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)} disabled={busy}
            >
              {REJECT_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <label htmlFor="reject-rationale" className="block text-sm font-medium text-ink">
              What did you see? <span className="text-ink-tertiary font-normal">(kept on the record; the candidate never sees it)</span>
            </label>
            <textarea
              id="reject-rationale" className="input resize-none" rows={3} maxLength={2000}
              value={rationale} onChange={(e) => setRationale(e.target.value)} disabled={busy}
              placeholder="One or two lines tying the call to the work you looked at."
            />
          </div>
        </div>
      )}

      {/*
        * REOPENING A REJECTION — the recovery from a mis-click that had no client path at all
        * until now. The edge is real (TRANSITIONS.rejected = { under_review }) and has always
        * demanded a written reason, so the confirm button below stays disabled until there is
        * something to record; sending a blank one would earn a 400 the recruiter never asked for.
        * The reason code is fixed rather than chosen (see REOPEN_REASON_CODE).
        */}
      {confirming === 'reopen' && (
        <div className="mb-5 space-y-3 max-w-lg">
          <div className="space-y-2">
            <label htmlFor="reopen-rationale" className="block text-sm font-medium text-ink">
              Why are you reopening this? <span className="text-ink-tertiary font-normal">(kept on the record; the candidate never sees it)</span>
            </label>
            <textarea
              id="reopen-rationale" className="input resize-none" rows={3} maxLength={2000}
              value={rationale} onChange={(e) => setRationale(e.target.value)} disabled={busy}
              placeholder="What changed, or what went wrong with the first call."
            />
          </div>
          <p className="text-sm text-ink-tertiary leading-relaxed">
            The rejection stays on the record — this appends a second decision beside it and puts
            the candidate back under review. Nothing reaches their inbox: of the moves here, only
            a rejection does that.
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {shortlistOffer && (
          confirming === 'shortlist' ? (
            <>
              <button type="button" className="btn-primary" onClick={() => void decide(shortlistOffer)} disabled={busy || blocked !== null || !isOwner}>
                {busy ? <InlineSpinner label="Shortlisting…" /> : 'Confirm shortlist'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setConfirming(null)} disabled={busy}>Cancel</button>
            </>
          ) : (
            <button type="button" className="btn-primary" onClick={() => setConfirming('shortlist')} disabled={busy || blocked !== null || !isOwner}>
              <CheckCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
              Shortlist
            </button>
          )
        )}

        {rejectOffer && (
          confirming === 'reject' ? (
            <>
              <button
                type="button" className="btn-danger" onClick={() => void decide(rejectOffer)}
                disabled={busy || blocked !== null || !isOwner || rationale.trim() === ''}
              >
                {busy ? <InlineSpinner label="Rejecting…" /> : 'Confirm rejection'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setConfirming(null)} disabled={busy}>Cancel</button>
            </>
          ) : (
            <button type="button" className="btn-secondary" onClick={() => setConfirming('reject')} disabled={busy || blocked !== null || !isOwner}>
              Reject
            </button>
          )
        )}

        {reopenOffer && (
          confirming === 'reopen' ? (
            <>
              <button
                type="button" className="btn-primary" onClick={() => void decide(reopenOffer)}
                disabled={busy || blocked !== null || !isOwner || rationale.trim() === ''}
              >
                {busy ? <InlineSpinner label="Reopening…" /> : 'Confirm reopen'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setConfirming(null)} disabled={busy}>Cancel</button>
            </>
          ) : (
            <button type="button" className="btn-primary" onClick={() => setConfirming('reopen')} disabled={busy || blocked !== null || !isOwner}>
              <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} />
              Reopen this rejection
            </button>
          )
        )}
      </div>

      {/* Nothing offerable, which today means `hired` or `withdrawn`. Saying so beats an empty
        * strip where two buttons used to sit and 409 on every press. */}
      {offers.length === 0 && (
        <p className="text-sm text-ink-tertiary leading-relaxed">
          There is nothing left for you to decide from <span className="text-ink">{stageLabel(stage)}</span>.
        </p>
      )}

      {/* requiresOwner is true on every recruiter edge, so a member sees which moves exist and
        * cannot press them. Told here, rather than left to discover it as a 403. */}
      {ownerBlocked && (
        <p className="mt-3 text-sm text-ink-tertiary leading-relaxed">
          Deciding is an owner action at this company. You can read the work and the screening;
          an owner makes the call.
        </p>
      )}

      {/*
        * What the CANDIDATE now sees, read off the response's `candidateStage` rather than
        * derived from the stage we asked for. This is the one place the two vocabularies sit
        * side by side, and it earns the line: `shortlisted` collapses to "In review", so a
        * recruiter who has just shortlisted someone can see for themselves that the candidate
        * has not been told they were. No email claim is made here — of the three moves this bar
        * sends, only the rejection mails anybody.
        */}
      {decided && (
        <p className="mt-4 text-sm text-ink-secondary leading-relaxed">
          Recorded as <span className="text-ink font-medium">{stageLabel(decided.decision.toStage)}</span>. The
          candidate&apos;s own page now reads &ldquo;{candidateStageLabel(decided.candidateStage)}&rdquo;.
        </p>
      )}
    </div>
  );
};

const ApplicantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  /* The role at the PICKED tenant, which is the tenant ?companyId= carries and therefore the one
   * this application belongs to (another company's id 404s before any of this renders). Read off
   * activeCompany rather than /api/me's `company`, which is membership row 0 whatever the picker
   * says — on a two-company recruiter that is how an owner here gets shown a member's screen. */
  const { activeCompany } = useAuth();

  const [loading, setLoading] = useState(true);
  const [loadNonce, setLoadNonce] = useState(0);
  const [loadError, setLoadError] = useState<{ message: string; notFound: boolean } | null>(null);
  const [detail, setDetail] = useState<ApplicationDetailData | null>(null);

  // Cancelled-guarded so a fast unmount (or a stale in-flight retry) can't
  // setState afterward — mirrors the load-by-id effect in JobManage.tsx.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const res = await hrApplicationDetail(id);
        if (cancelled) return;
        setDetail(res);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setLoadError({ message: err.message, notFound: err.status === 404 });
        } else {
          setLoadError({ message: 'Check your connection and try again.', notFound: false });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loadNonce]);

  // The stage the SERVER recorded, off the decision response — not the stage the click asked
  // for. Keeping the rendered row in step matters beyond cosmetics: it is the row the next
  // decision's expectedStage is read from.
  const handleDecided = (result: HrDecisionResult) => {
    setDetail((prev) => (prev ? { ...prev, application: { ...prev.application, stage: result.decision.toStage } } : prev));
  };

  const handleRescreenQueued = (submissionId: string) => {
    setDetail((prev) => (prev ? markScreeningQueued(prev, submissionId) : prev));
  };

  if (loading) return <LoadingNotice />;
  if (loadError?.notFound) return <NotFoundNotice />;
  if (loadError) return <LoadErrorNotice message={loadError.message} onRetry={() => setLoadNonce((n) => n + 1)} />;
  if (!detail) return null;

  const { application, candidate, resume, submissions } = detail;
  const profileEntries = PROFILE_FIELD_ORDER
    .map((key) => ({ key, value: candidate.profile?.[key] }))
    .filter((entry): entry is { key: string; value: string } => !!entry.value);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <StageChip stage={application.stage} />
        {viewedLabel(application.firstViewedAt) && (
          <span className="chip" style={{ backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-tertiary)' }}>
            {viewedLabel(application.firstViewedAt)}
          </span>
        )}
        <span className="text-xs text-ink-tertiary">Applied {formatRelativeDate(application.createdAt)}</span>
      </div>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-1">{candidate.name}</h1>
      <p className="text-sm text-ink-secondary mb-8">{candidate.email}</p>

      {profileEntries.length > 0 && (
        <div className="card mb-6">
          <span className="label-editorial block mb-4">Profile</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {profileEntries.map(({ key, value }) => (
              <div key={key}>
                <span className="label-editorial block mb-1">{PROFILE_FIELD_LABEL[key] ?? key}</span>
                <p className="text-sm text-ink whitespace-pre-line">{value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {resume && (
        <div className="card mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
            <span className="label-editorial">Resume</span>
            {resume.downloadUrl ? (
              <a
                href={resume.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
              >
                <Download className="h-3 w-3" strokeWidth={1.75} />
                Download resume
              </a>
            ) : (
              <span className="text-ink-tertiary text-sm">Download unavailable</span>
            )}
          </div>
          <p className="text-sm text-ink-tertiary mb-3">{resume.originalFilename}</p>
          {resume.parseStatus === 'failed' && (
            <p className="text-xs text-ink-tertiary mb-3">
              We couldn&apos;t parse this resume automatically — download it to review directly.
            </p>
          )}
          {resume.parsed && (
            <div className="space-y-4">
              {resume.parsed.skills.length > 0 && (
                <div>
                  <span className="label-editorial block mb-2">Skills</span>
                  <div className="flex flex-wrap gap-2">
                    {resume.parsed.skills.map((skill) => (
                      <span
                        key={skill}
                        className="chip"
                        style={{ backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {resume.parsed.experience.length > 0 && (
                <div>
                  <span className="label-editorial block mb-2">Experience</span>
                  <ul className="space-y-1">
                    {resume.parsed.experience.map((exp, i) => (
                      <li key={i} className="text-sm text-ink">
                        <span className="font-medium">{exp.title}</span>
                        {exp.company && <span className="text-ink-tertiary"> · {exp.company}</span>}
                        {exp.years && <span className="text-ink-tertiary"> · {exp.years}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {resume.parsed.education.length > 0 && (
                <div>
                  <span className="label-editorial block mb-2">Education</span>
                  <ul className="space-y-1">
                    {resume.parsed.education.map((edu, i) => (
                      <li key={i} className="text-sm text-ink">
                        {edu}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div>
        <span className="label-editorial block mb-4">Submissions ({submissions.length})</span>
        {submissions.length === 0 ? (
          <p className="text-sm text-ink-tertiary">No tasks submitted.</p>
        ) : (
          <div className="space-y-4">
            {submissions.map((s) => (
              <SubmissionCard key={s.id} submission={s} onRescreenQueued={handleRescreenQueued} />
            ))}
          </div>
        )}
      </div>

      <ActionBar
        applicationId={application.id}
        stage={application.stage}
        aggregateVersion={application.aggregateVersion}
        companyRole={activeCompany?.companyRole ?? null}
        onDecided={handleDecided}
        onReload={() => setLoadNonce((n) => n + 1)}
      />
    </div>
  );
};

export default ApplicantDetail;
